/**
 * StepIndicator - 创作步骤导航条
 */

import { CheckCircle, Type, Volume2, Image, Settings } from 'lucide-react';

interface Step {
  id: number;
  label: string;
  icon: React.ReactNode;
}

const STEPS: Step[] = [
  { id: 0, label: '配置', icon: <Settings className="w-4 h-4" /> },
  { id: 1, label: '文案', icon: <Type className="w-4 h-4" /> },
  { id: 2, label: '音频', icon: <Volume2 className="w-4 h-4" /> },
  { id: 3, label: '插图', icon: <Image className="w-4 h-4" /> },
  { id: 4, label: '完成', icon: <CheckCircle className="w-4 h-4" /> },
];

interface StepIndicatorProps {
  currentStep: number;
}

export function StepIndicator({ currentStep }: StepIndicatorProps) {
  const getStepStatus = (stepId: number) => {
    if (stepId < currentStep) return 'completed';
    if (stepId === currentStep) return 'current';
    return 'pending';
  };

  return (
    <div className="w-full flex justify-center">
      {/* 桌面端 */}
      <div className="hidden md:flex items-center justify-between" style={{ width: '100%', maxWidth: '900px' }}>
        {STEPS.map((step, index) => {
          const status = getStepStatus(index);
          const isLast = index === STEPS.length - 1;

          return (
            <div key={step.id} className="flex items-center flex-1">
              <div className="flex flex-col items-center">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                    status === 'completed' ? 'bg-green-500 text-white' :
                    status === 'current' ? 'bg-blue-500 text-white ring-4 ring-blue-100' :
                    'bg-gray-200 text-gray-400'
                  }`}
                >
                  {status === 'completed' ? <CheckCircle className="w-5 h-5" /> : step.icon}
                </div>
                <span
                  className={`mt-2 text-sm font-medium ${
                    status === 'completed' ? 'text-green-600' :
                    status === 'current' ? 'text-blue-600' :
                    'text-gray-400'
                  }`}
                >
                  {step.label}
                </span>
              </div>

              {!isLast && (
                <div className="flex-1 mx-4">
                  <div className={`h-1 rounded-full ${index < currentStep ? 'bg-green-500' : 'bg-gray-200'}`} />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* 移动端 */}
      <div className="md:hidden">
        <div className="flex items-center justify-between mb-4">
          <span className="text-lg font-semibold text-gray-900">
            步骤 {currentStep + 1} / {STEPS.length}
          </span>
          <span className="text-sm text-gray-500">{STEPS[currentStep]?.label}</span>
        </div>
        <div className="flex gap-1">
          {STEPS.map((step, index) => {
            const status = getStepStatus(index);
            return (
              <div
                key={step.id}
                className={`flex-1 h-2 rounded-full ${
                  status === 'completed' ? 'bg-green-500' :
                  status === 'current' ? 'bg-blue-500' :
                  'bg-gray-200'
                }`}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default StepIndicator;
